import { useState, useEffect, useCallback } from 'react';
import {
  Stack,
  TextInput,
  Text,
  Divider,
  Group,
  Badge,
  ScrollArea,
  Loader,
  Center,
  Alert,
  ActionIcon,
  Skeleton,
  Paper,
  Button,
  Chip,
  Modal,
  Textarea,
  Box,
} from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconSearch,
  IconTicket,
  IconBuilding,
  IconUser,
  IconCalendar,
  IconClockHour4,
  IconAlertCircle,
  IconX,
  IconRefresh,
  IconChevronDown,
  IconFilter,
  IconPlus,
} from '@tabler/icons-react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { api } from '../lib/api';
import type { WeclappTicket } from '../lib/api/types/weclapp';
import { formatDateTimeShort } from '../lib/formatters';
import { weclappStatusColor } from '../lib/ticketStatusColor';

interface WeclappTicketPickerProps {
  value?: string;
  selectedTicketData?: WeclappTicket;
  onChange: (ticketId: string | undefined, ticket?: WeclappTicket) => void;
  placeholder?: string;
  /** Weclapp party ID — pre-filters tickets to this customer */
  weclappCustomerId?: string;
}

const PAGE_SIZE = 50;

function isClosedTicket(ticket: WeclappTicket): boolean {
  return ticket.status?.toLowerCase().includes('geschloss') === true;
}

export function WeclappTicketPicker({
  value,
  selectedTicketData,
  onChange,
  placeholder = 'In Weclapp suchen...',
  weclappCustomerId,
}: WeclappTicketPickerProps) {
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebouncedValue(search, 500);
  const [page, setPage] = useState(1);
  const [allTickets, setAllTickets] = useState<WeclappTicket[]>([]);
  const [hasMore, setHasMore] = useState(true);
  const [showClosed, setShowClosed] = useState(false);
  const [showAllCustomers, setShowAllCustomers] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [newSubject, setNewSubject] = useState('');

  const [internalSelected, setInternalSelected] = useState<WeclappTicket | undefined>(
    selectedTicketData
  );
  const selected = selectedTicketData ?? internalSelected;

  const activeCustomerId = weclappCustomerId && !showAllCustomers ? weclappCustomerId : undefined;

  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['weclappTickets', debouncedSearch, page, activeCustomerId],
    queryFn: () => api.searchWeclappTickets(debouncedSearch || undefined, page, PAGE_SIZE, activeCustomerId),
    staleTime: 30_000,
  });

  useEffect(() => {
    setPage(1);
    setAllTickets([]);
  }, [debouncedSearch, activeCustomerId]);

  useEffect(() => {
    if (data?.tickets) {
      if (page === 1) {
        setAllTickets(data.tickets);
      } else {
        setAllTickets((prev) => {
          const existingIds = new Set(prev.map((t) => t.id));
          return [...prev, ...data.tickets.filter((t) => !existingIds.has(t.id))];
        });
      }
      setHasMore(data.tickets.length >= PAGE_SIZE);
    }
  }, [data?.tickets, page]);

  useEffect(() => {
    if (selectedTicketData) return;
    if (value && (!internalSelected || internalSelected.id !== value)) {
      const ticket = allTickets.find((t) => t.id === value);
      if (ticket) setInternalSelected(ticket);
    }
    if (!value && internalSelected) setInternalSelected(undefined);
  }, [value, allTickets, selectedTicketData, internalSelected]);

  const handleSelect = useCallback(
    (ticket: WeclappTicket) => {
      setInternalSelected(ticket);
      onChange(ticket.id, ticket);
      setSearch('');
    },
    [onChange]
  );

  const handleClear = useCallback(() => {
    setInternalSelected(undefined);
    onChange(undefined);
  }, [onChange]);

  const handleLoadMore = useCallback(() => {
    setPage((p) => p + 1);
  }, []);

  const createMutation = useMutation({
    mutationFn: (subject: string) =>
      api.createWeclappTicket({ partyId: weclappCustomerId!, subject }),
    onSuccess: (result) => {
      notifications.show({ title: 'Ticket angelegt', message: `#${result.ticketNumber}`, color: 'teal' });
      setCreateOpen(false);
      setNewSubject('');
      // Reload and auto-select
      refetch().then((r) => {
        const created = r.data?.tickets.find((t) => t.id === result.id);
        if (created) handleSelect(created);
      });
    },
    onError: () => notifications.show({ title: 'Fehler', message: 'Ticket konnte nicht angelegt werden.', color: 'red' }),
  });

  const visibleTickets = showClosed ? allTickets : allTickets.filter((t) => !isClosedTicket(t));
  const closedCount = allTickets.length - visibleTickets.length;

  if (selected) {
    return (
      <Paper withBorder p="md">
        <Group justify="space-between" align="flex-start" wrap="nowrap">
          <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
            <Group gap="xs" wrap="nowrap">
              <Badge size="lg" variant="filled" color="violet">
                #{selected.ticketNumber}
              </Badge>
              {selected.status && (
                <Badge size="sm" color="blue" variant="light">
                  {selected.status}
                </Badge>
              )}
            </Group>
            <Text size="sm" fw={500} lineClamp={2}>
              {selected.subject}
            </Text>
            <Group gap="md" wrap="nowrap">
              {selected.partyName && (
                <Group gap={4} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                  <IconBuilding size={12} style={{ opacity: 0.6, flexShrink: 0 }} />
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {selected.partyName}
                  </Text>
                </Group>
              )}
              {selected.contactName && (
                <Group gap={4} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                  <IconUser size={12} style={{ opacity: 0.6, flexShrink: 0 }} />
                  <Text size="xs" c="dimmed" lineClamp={1}>
                    {selected.contactName}
                  </Text>
                </Group>
              )}
              {selected.createdDate && (
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  <IconCalendar size={12} style={{ opacity: 0.6 }} />
                  <Text size="xs" c="dimmed">
                    {formatDateTimeShort(selected.createdDate)}
                  </Text>
                </Group>
              )}
              {selected.lastModifiedDate && (
                <Group gap={4} wrap="nowrap" style={{ flexShrink: 0 }}>
                  <IconClockHour4 size={12} style={{ opacity: 0.6 }} />
                  <Text size="xs" c="dimmed">
                    {formatDateTimeShort(selected.lastModifiedDate)}
                  </Text>
                </Group>
              )}
            </Group>
          </Stack>
          <ActionIcon
            variant="subtle"
            color="gray"
            onClick={handleClear}
            title="Auswahl aufheben"
          >
            <IconX size={18} />
          </ActionIcon>
        </Group>
      </Paper>
    );
  }

  return (
    <>
      <Stack gap="sm">
        {/* Filter chips */}
        {weclappCustomerId && (
          <Group gap="xs" align="center">
            <IconFilter size={14} style={{ opacity: 0.5 }} />
            <Chip
              size="xs"
              checked={showAllCustomers}
              onChange={setShowAllCustomers}
            >
              Alle Kunden
            </Chip>
            <Chip
              size="xs"
              checked={showClosed}
              onChange={setShowClosed}
            >
              Geschlossene anzeigen
            </Chip>
          </Group>
        )}

        {/* Search input */}
        <TextInput
          placeholder={placeholder}
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
          leftSection={<IconSearch size={16} />}
          rightSection={
            isFetching ? (
              <Loader size={16} />
            ) : (
              <ActionIcon variant="subtle" size="sm" onClick={() => refetch()} title="Neu laden">
                <IconRefresh size={14} />
              </ActionIcon>
            )
          }
          size="md"
        />

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
            Fehler beim Laden der Weclapp-Tickets.
          </Alert>
        )}

        <Paper withBorder style={{ overflow: 'hidden' }}>
        <ScrollArea.Autosize mah={320} offsetScrollbars>
          <Stack gap={0} px="xs">
            {isLoading && !allTickets.length ? (
              Array.from({ length: 4 }).map((_, i) => (
                <Box key={i} py="xs" px={4}>
                  <Stack gap={4}>
                    <Skeleton height={16} width="25%" />
                    <Skeleton height={14} width="80%" />
                    <Skeleton height={11} width="45%" />
                  </Stack>
                  {i < 3 && <Divider mt="xs" />}
                </Box>
              ))
            ) : visibleTickets.length === 0 ? (
              <Center py="lg">
                <Stack align="center" gap="xs">
                  <IconTicket size={32} style={{ opacity: 0.3 }} />
                  <Text size="sm" c="dimmed">
                    {debouncedSearch ? 'Keine Tickets gefunden' : 'Keine offenen Tickets'}
                  </Text>
                  {!showClosed && closedCount > 0 && (
                    <Text size="xs" c="dimmed">
                      {closedCount} geschlossene ausgeblendet
                    </Text>
                  )}
                </Stack>
              </Center>
            ) : (
              visibleTickets.map((ticket, idx) => (
                <Box key={ticket.id}>
                  <Box
                    py="xs"
                    px={4}
                    style={{ cursor: 'pointer' }}
                    onClick={() => handleSelect(ticket)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === 'Enter' && handleSelect(ticket)}
                    aria-label={`Ticket ${ticket.ticketNumber}: ${ticket.subject}`}
                  >
                    <Stack gap={2}>
                      <Group gap="xs" wrap="nowrap">
                        <Text size="sm" fw={700} c="violet" style={{ flexShrink: 0 }}>
                          #{ticket.ticketNumber}
                        </Text>
                        {ticket.status && (
                          <Badge size="xs" color={weclappStatusColor(ticket.status)} variant="light">
                            {ticket.status}
                          </Badge>
                        )}
                        {ticket.priority && (
                          <Badge size="xs" color="gray" variant="dot">
                            {ticket.priority}
                          </Badge>
                        )}
                      </Group>
                      <Text size="sm" lineClamp={1} fw={500}>
                        {ticket.subject}
                      </Text>
                      <Group gap="sm" wrap="nowrap">
                        {ticket.partyName && (
                          <Group gap={3} wrap="nowrap" style={{ minWidth: 0, flex: 1 }}>
                            <IconBuilding size={11} style={{ opacity: 0.5, flexShrink: 0 }} />
                            <Text size="xs" c="dimmed" lineClamp={1}>
                              {ticket.partyName}
                            </Text>
                          </Group>
                        )}
                        {ticket.createdDate && (
                          <Group gap={3} wrap="nowrap" style={{ flexShrink: 0 }}>
                            <IconCalendar size={11} style={{ opacity: 0.5 }} />
                            <Text size="xs" c="dimmed">
                              {formatDateTimeShort(ticket.createdDate)}
                            </Text>
                          </Group>
                        )}
                      </Group>
                    </Stack>
                  </Box>
                  {idx < visibleTickets.length - 1 && <Divider />}
                </Box>
              ))
            )}
          </Stack>
        </ScrollArea.Autosize>
        </Paper>

        <Group justify="space-between" align="center">
          <Text size="xs" c="dimmed">
            {visibleTickets.length} Tickets
            {!showClosed && closedCount > 0 ? ` (+ ${closedCount} geschlossene)` : ''}
            {data?.totalCount ? ` von ${data.totalCount}` : ''}
          </Text>
          {isFetching && <Loader size={12} />}
        </Group>

        {hasMore && (
          <Button
            variant="light"
            color="gray"
            size="sm"
            onClick={handleLoadMore}
            loading={isFetching}
            leftSection={<IconChevronDown size={16} />}
            fullWidth
          >
            Weitere Tickets laden
          </Button>
        )}

        {weclappCustomerId && (
          <Button
            variant="light"
            color="teal"
            size="sm"
            leftSection={<IconPlus size={16} />}
            onClick={() => setCreateOpen(true)}
            fullWidth
          >
            Neues Ticket anlegen
          </Button>
        )}
      </Stack>

      <Modal
        opened={createOpen}
        onClose={() => { setCreateOpen(false); setNewSubject(''); }}
        title="Neues Weclapp-Ticket anlegen"
        size="sm"
        centered
      >
        <Stack gap="sm">
          <Textarea
            label="Betreff"
            placeholder="Kurze Beschreibung des Anliegens…"
            value={newSubject}
            onChange={(e) => setNewSubject(e.currentTarget.value)}
            autosize
            minRows={2}
            maxRows={4}
            maxLength={255}
            required
            autoFocus
          />
          <Group justify="flex-end" mt="xs">
            <Button variant="subtle" onClick={() => { setCreateOpen(false); setNewSubject(''); }}>
              Abbrechen
            </Button>
            <Button
              color="teal"
              disabled={!newSubject.trim()}
              loading={createMutation.isPending}
              onClick={() => createMutation.mutate(newSubject.trim())}
            >
              Ticket anlegen
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
